import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  PageContainer,
  MainContent,
  ContentSection,
  SectionTitle,
  Heading2,
  Text,
  Button
} from '../../styles/StyledComponents';
import { ThemeColors, Spacing } from '../../styles/ThemeStyles';
import { ActivityIndicator } from '../../components/common';
import { migrateNotesInBrowser } from '../../scripts/migrateNotesBrowser';
import styled from 'styled-components';
import { Database, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

const MigrationContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: ${Spacing.xl};
`;

const InfoCard = styled.div`
  background: ${ThemeColors.background.secondary};
  border: 1px solid ${ThemeColors.border};
  border-radius: 8px;
  padding: ${Spacing.lg};
  margin-bottom: ${Spacing.lg};
`;

const StatusCard = styled.div<{ type: 'info' | 'success' | 'error' | 'warning' }>`
  background: ${props => {
    switch (props.type) {
      case 'success': return ThemeColors.success.light;
      case 'error': return ThemeColors.error.light;
      case 'warning': return ThemeColors.warning.light;
      default: return ThemeColors.info.light;
    }
  }};
  border: 1px solid ${props => {
    switch (props.type) {
      case 'success': return ThemeColors.success.main;
      case 'error': return ThemeColors.error.main;
      case 'warning': return ThemeColors.warning.main;
      default: return ThemeColors.info.main;
    }
  }};
  border-radius: 8px;
  padding: ${Spacing.lg};
  margin-bottom: ${Spacing.md};
  display: flex;
  align-items: flex-start;
  gap: ${Spacing.md};
`;

const StatusIcon = styled.div`
  flex-shrink: 0;
  margin-top: 4px;
`;

const StatusContent = styled.div`
  flex: 1;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${Spacing.md};
  margin-top: ${Spacing.lg};
`;

const StatCard = styled.div`
  background: ${ThemeColors.background.secondary};
  border: 1px solid ${ThemeColors.border};
  border-radius: 8px;
  padding: ${Spacing.lg};
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 32px;
  font-weight: bold;
  color: ${ThemeColors.primary.main};
  margin-bottom: ${Spacing.xs};
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: ${ThemeColors.text.secondary};
`;

const ActionButton = styled(Button)`
  width: 100%;
  max-width: 300px;
  margin: ${Spacing.md} auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.sm};
  font-size: 16px;
  padding: ${Spacing.md} ${Spacing.xl};
`;

const LogContainer = styled.div`
  background: ${ThemeColors.background.primary};
  border: 1px solid ${ThemeColors.border};
  border-radius: 8px;
  padding: ${Spacing.md};
  max-height: 400px;
  overflow-y: auto;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  margin-top: ${Spacing.lg};
`;

const LogLine = styled.div<{ type?: 'success' | 'error' | 'info' }>`
  padding: ${Spacing.xs} 0;
  color: ${props => {
    switch (props.type) {
      case 'success': return ThemeColors.success.main;
      case 'error': return ThemeColors.error.main;
      default: return ThemeColors.text.primary;
    }
  }};
`;

interface MigrationResult {
  success: boolean;
  totalStores?: number;
  storesWithNotes?: number;
  totalNotesMigrated?: number;
  storesCleaned?: number;
  totalErrors?: number;
  error?: string;
}

export const NotesMigrationScreen: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<MigrationResult | null>(null);
  const [logs, setLogs] = useState<string[]>([]);

  const addLog = (message: string, type?: 'success' | 'error' | 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, `[${timestamp}] ${message}`]);
  };

  const handleMigrate = async () => {
    setIsRunning(true);
    setResult(null);
    setLogs([]);
    
    addLog('Starting notes migration...', 'info');
    
    try {
      // Capture console.log output
      const originalLog = console.log;
      const originalError = console.error;
      
      console.log = (...args: any[]) => {
        originalLog(...args);
        addLog(args.map(arg => typeof arg === 'string' ? arg : JSON.stringify(arg)).join(' '), 'info');
      };
      
      console.error = (...args: any[]) => {
        originalError(...args);
        addLog(args.map(arg => typeof arg === 'string' ? arg : JSON.stringify(arg)).join(' '), 'error');
      };

      const migrationResult = await migrateNotesInBrowser();
      
      // Restore original console functions
      console.log = originalLog;
      console.error = originalError;

      setResult(migrationResult);
      
      if (migrationResult.success) {
        addLog('Migration completed successfully!', 'success');
      } else {
        addLog(`Migration failed: ${migrationResult.error}`, 'error');
      }
    } catch (error: any) {
      addLog(`Migration error: ${error.message}`, 'error');
      setResult({
        success: false,
        error: error.message
      });
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <PageContainer>
      <MainContent>
        <ContentSection>
          <SectionTitle>
            <Database size={24} />
            Notes Migration Tool
          </SectionTitle>

          <MigrationContainer>
            <InfoCard>
              <Heading2 style={{ marginBottom: Spacing.md }}>
                What does this do?
              </Heading2>
              <Text style={{ marginBottom: Spacing.sm }}>
                This tool migrates all notes from the old storage format (stored directly in store documents) 
                to the new format (stored in a subcollection: <code>Stores/{'{storeId}'}/notes/{'{noteId}'}</code>).
              </Text>
              <Text style={{ marginBottom: Spacing.sm }}>
                <strong>Important:</strong>
              </Text>
              <ul style={{ marginLeft: Spacing.lg, marginTop: Spacing.sm }}>
                <li>This migration is <strong>safe to run multiple times</strong> - it will skip notes that already exist</li>
                <li>All note data will be preserved (text, media, documents, replies, dates)</li>
                <li>The migration may take a few minutes depending on the number of stores and notes</li>
                <li>You can continue using the app while the migration runs</li>
              </ul>
            </InfoCard>

            {result && (
              <StatusCard type={result.success ? 'success' : 'error'}>
                <StatusIcon>
                  {result.success ? (
                    <CheckCircle size={24} color={ThemeColors.success.main} />
                  ) : (
                    <XCircle size={24} color={ThemeColors.error.main} />
                  )}
                </StatusIcon>
                <StatusContent>
                  <Heading2 style={{ marginBottom: Spacing.xs }}>
                    {result.success ? 'Migration Completed!' : 'Migration Failed'}
                  </Heading2>
                  {result.success ? (
                    <Text>
                      Successfully migrated {result.totalNotesMigrated} notes from {result.storesWithNotes} stores.
                      {result.storesCleaned !== undefined && result.storesCleaned > 0 && (
                        <span style={{ display: 'block', marginTop: Spacing.xs }}>
                          Cleaned {result.storesCleaned} store document(s) by removing old notes field.
                        </span>
                      )}
                      {result.totalErrors > 0 && (
                        <span style={{ color: ThemeColors.warning.main, display: 'block', marginTop: Spacing.xs }}>
                          Note: {result.totalErrors} errors occurred during migration.
                        </span>
                      )}
                    </Text>
                  ) : (
                    <Text>{result.error}</Text>
                  )}
                </StatusContent>
              </StatusCard>
            )}

            {result?.success && (
              <StatsGrid>
                <StatCard>
                  <StatValue>{result.totalStores || 0}</StatValue>
                  <StatLabel>Total Stores</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue>{result.storesWithNotes || 0}</StatValue>
                  <StatLabel>Stores with Notes</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue>{result.totalNotesMigrated || 0}</StatValue>
                  <StatLabel>Notes Migrated</StatLabel>
                </StatCard>
                {result.storesCleaned !== undefined && result.storesCleaned > 0 && (
                  <StatCard>
                    <StatValue style={{ color: ThemeColors.success.main }}>
                      {result.storesCleaned}
                    </StatValue>
                    <StatLabel>Stores Cleaned</StatLabel>
                  </StatCard>
                )}
                {result.totalErrors !== undefined && result.totalErrors > 0 && (
                  <StatCard>
                    <StatValue style={{ color: ThemeColors.error.main }}>
                      {result.totalErrors}
                    </StatValue>
                    <StatLabel>Errors</StatLabel>
                  </StatCard>
                )}
              </StatsGrid>
            )}

            <div style={{ textAlign: 'center', marginTop: Spacing.xl }}>
              <ActionButton
                onClick={handleMigrate}
                disabled={isRunning}
                variant="primary"
              >
                {isRunning ? (
                  <>
                    <ActivityIndicator size="small" />
                    Migrating Notes...
                  </>
                ) : (
                  <>
                    <Database size={20} />
                    Start Migration
                  </>
                )}
              </ActionButton>
            </div>

            {logs.length > 0 && (
              <LogContainer>
                <Heading2 style={{ marginBottom: Spacing.md, fontSize: 16 }}>
                  Migration Log
                </Heading2>
                {logs.map((log, index) => (
                  <LogLine 
                    key={index}
                    type={
                      log.includes('✅') || log.includes('success') ? 'success' :
                      log.includes('❌') || log.includes('Error') || log.includes('error') ? 'error' :
                      'info'
                    }
                  >
                    {log}
                  </LogLine>
                ))}
              </LogContainer>
            )}

            <div style={{ textAlign: 'center', marginTop: Spacing.xl }}>
              <Button
                onClick={() => navigate('/manager/dashboard')}
                variant="secondary"
              >
                Back to Dashboard
              </Button>
            </div>
          </MigrationContainer>
        </ContentSection>
      </MainContent>
    </PageContainer>
  );
};

